import { Component, EventEmitter, Input, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragDropModule, moveItemInArray, CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { User } from '@lmc-app/shared/interface/user.interface';
import dayjs from 'dayjs';

interface FieldState {
  field: string;
  value: string;
}

interface DeliveryState {
  Email: boolean;
  SMS: boolean;
  WhatsApp: boolean;
  // // URL: boolean;
}

interface Recipients {
  role: string;
  name: FieldState;
  firstName: FieldState;
  middleName: FieldState;
  lastName: FieldState;
  email: FieldState;
  mobile: FieldState;
  delivery_option: DeliveryState;
}

@Component({
  selector: 'app-agreements-recipients',
  standalone: true,
  imports: [CommonModule, DragDropModule, MatIconModule, FormsModule, MatInputModule, MatFormFieldModule, MatCheckboxModule, MatProgressSpinnerModule],
  templateUrl: './agreements-recipients.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./agreements-recipients.component.scss']
})
export class AgreementsRecipientsComponent {
  @Input() appData: any;
  @Input() application: any;
  @Input() currentUser: User;
  @Output() applicationChange = new EventEmitter();
  // @Output() reArrangedRecipients = new EventEmitter<Recipients[]>();

  recipients: Recipients[] = [];
  editingIndex: number = -1;
  editingField: string = '';
  tempValue: string = ''; // Populates input field via [(ngModel)]
  savingIndex: number = -1;
  savingField: string = '';

  constructor(
    private _docusignService: DocusignService,   
  ){}
  ngOnChanges(changes: SimpleChanges) {
    if(changes.application?.currentValue){
      this.application = changes.application?.currentValue;      
    }
    
    if (changes.currentUser?.currentValue) {
      this.currentUser = changes.currentUser?.currentValue;
    }
    
    if (changes.appData?.currentValue) {
      this.appData = changes.appData?.currentValue;
      this.reapplyPreviousOrder(this.appData?.recipients);
      this.recipients = this.appData?.recipients;
      this.normalizeDeliveryOptions();
    }
  }

  // Reapply the same order/ new order after save
  private reapplyPreviousOrder(newRecipients: Recipients[]): void {
    if (!newRecipients?.length || !this.recipients?.length) {
      return;
    }

    const recipientKey = (r: Recipients) => `${r.role}|${r.email?.value}`;
    const previousOrder = this.recipients.map(recipientKey);

    newRecipients.sort((a, b) => {
      const indexA = previousOrder.indexOf(recipientKey(a));
      const indexB = previousOrder.indexOf(recipientKey(b));
      return (indexA === -1 ? previousOrder.length : indexA) - (indexB === -1 ? previousOrder.length : indexB);
    });
  }

  private normalizeDeliveryOptions() {
    this.recipients?.forEach((r) => {
      const options = r.delivery_option;
      if (!options) return;
      const objectKeys = Object.keys(options);
      const realEmailKey = objectKeys.find((k) => k.toLowerCase() === 'email') || 'email';
      const allFalse = Object.values(options).every((value) => value === false);
      if (allFalse) {
        options[realEmailKey] = true;
      }
    });
  }

  drop(event: CdkDragDrop<Recipients[]>) {
    moveItemInArray(this.recipients, event.previousIndex, event.currentIndex);
    this.cancelEdit(); 
  }

  enableEdit(index: number, field: string, currentValue: string, roleName: string) {
    // No editing allowed on Manager and Sales
    if(['Manager','Agent'].includes(roleName)) return;

    this.editingIndex = index;
    this.editingField = field;
    this.tempValue = currentValue; 
  }

  // Restrict keys to 'name' or 'email' since 'main' is a string
  saveEdit(index: number, fieldName: 'name' | 'firstName' | 'middleName' | 'lastName' | 'email' | 'mobile' ) {
    if (this.tempValue.trim() !== '' || fieldName === 'middleName') {
      this.recipients[index][fieldName].value = this.tempValue;
      const updateFieldName = this.recipients[index][fieldName].field;
      const DealNameAPIFieldName = this.recipients[index]['name'].field;    // Create DealName update when firstname or middlename or lastname updated

      // Combine names to form DealName
      const DealNameValue = (['firstName','middleName','lastName'].includes(fieldName))
      ?  { [DealNameAPIFieldName]: [this.recipients[index]['firstName'].value,this.recipients[index]['middleName'].value,this.recipients[index]['lastName'].value].filter(Boolean).join(' ') }
      : null;

      // Add DealName to the object if changed
      const zohoUpdateData = {
        [updateFieldName]: this.tempValue,
        ...(!!DealNameValue ? DealNameValue : ''),
        Override_Type: `${this.currentUser?.full_name} (${dayjs().format('YYYY-MM-DD HH:mm:ss')})`
      };

      this.savingIndex = index;
      this.savingField = fieldName;

      this._docusignService.saveDocusignZohoChanges(this.appData?.ApplicationId?.value,zohoUpdateData,this.appData?.ContactId?.value).subscribe({
        next: res => {
          this.applicationChange.emit(this.application);
        },
        error: () => {
          this.savingIndex = -1;
          this.savingField = '';
        },
        complete: () => {
          this.savingIndex = -1;
          this.savingField = '';
        }
      });
    }
    this.cancelEdit();
  }

  isSaving(index: number, field: string): boolean {
    return this.savingIndex === index && this.savingField === field;
  }

  cancelEdit() {
    this.editingIndex = -1;
    this.editingField = '';
    this.tempValue = '';
  }

  onToggle(index: number, key: string, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    const deliveryOptions = this.recipients[index].delivery_option;

    // 1. Find the exact case-sensitive keys present in your object
    const objectKeys = Object.keys(deliveryOptions);
    const realEmailKey = objectKeys.find(k => k.toLowerCase() === 'email') || 'email';
    const realWhatsappKey = objectKeys.find(k => k.toLowerCase() === 'whatsapp') || 'whatsapp';
    const realSmsKey = objectKeys.find(k => k.toLowerCase() === 'sms') || 'sms';
    // // const realURLKey = objectKeys.find(k => k.toLowerCase() === 'url') || 'url';

    // 2. Apply the user's intended change
    deliveryOptions[key] = isChecked;

    // 3. RULE 1: Can't be empty (Default to Email)
    const allFalse = Object.values(deliveryOptions).every(value => value === false);
    if (allFalse) {
      deliveryOptions[realEmailKey] = true;
      // // deliveryOptions[realURLKey] = false;
      if (key.toLowerCase() === 'email') {
        checkbox.checked = true;
      }
      return; 
    }

    // 4. RULE 2: SMS and WhatsApp can't be active together
    if (key.toLowerCase() === 'whatsapp' && isChecked) {
      // If turning on WhatsApp, turn off SMS
      deliveryOptions[realSmsKey] = false;
      // // deliveryOptions[realURLKey] = false;
    }
    if (key.toLowerCase() === 'sms' && isChecked) {
      // If turning on SMS, turn off WhatsApp
      deliveryOptions[realWhatsappKey] = false;
      // // deliveryOptions[realURLKey] = false;
    }

    // 5. RULE 3: WhatsApp requires Email
    if (deliveryOptions[realWhatsappKey] === true) {
      // If WhatsApp ended up active, Email MUST be active
      deliveryOptions[realEmailKey] = true;
      // // deliveryOptions[realURLKey] = false;
    }
    if (key.toLowerCase() === 'email' && !isChecked) {
      // If user explicitly unchecked Email, kill WhatsApp
      deliveryOptions[realWhatsappKey] = false;
      // // deliveryOptions[realURLKey] = false;
    }
    // // if (key.toLowerCase() === 'url' && isChecked) {
    // //   deliveryOptions[realURLKey] = true;
    // //   deliveryOptions[realEmailKey] = false;
    // //   deliveryOptions[realSmsKey] = false;
    // //   deliveryOptions[realWhatsappKey] = false;
    // // }
  }

}

